import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Download, RefreshCw, RotateCcw, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { T, STATUS_STYLE, BADGES, badgeClass, featureLabel, fmtDuration, fmtTime, shortId } from './aiOpsI18n';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai-operations`;
export const authCfg = () => {
  const t = localStorage.getItem('token');
  return { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} };
};
export const useLang = () => {
  const [lang, setLang] = useState(() => localStorage.getItem('ai_ops_lang') === 'en' ? 'en' : 'ja');
  const toggle = () => { const n = lang === 'en' ? 'ja' : 'en'; localStorage.setItem('ai_ops_lang', n); setLang(n); };
  return [lang, toggle, T[lang]];
};
export const copyText = async (v, t) => { try { await navigator.clipboard.writeText(String(v)); toast.success(t.copied); } catch { toast.error('copy failed'); } };

export const StatusBadge = ({ status, t }) => (
  <span data-testid="aiops-status-badge" className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[status] || STATUS_STYLE.cancelled}`}>
    <span aria-hidden>{{ complete: '✓', complete_with_warnings: '!', failed: '✕', running: '…', rejected: '⊘' }[status] || '·'}</span>
    {t[status] || status || t.notRecorded}
  </span>
);
export const Badge = ({ code, lang }) => { const b = BADGES[code]; return b ? (
  <span data-testid={`aiops-badge-${code}`} className={`whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-medium ${badgeClass(b[2])}`}>{b[lang === 'en' ? 1 : 0]}</span>) : null; };
export const IdChip = ({ id, t }) => (id ? (
  <span className="inline-flex items-center gap-1 font-mono text-[11px] text-zinc-700" title={id}>{shortId(id)}
    <button type="button" data-testid="aiops-copy-id" onClick={() => copyText(id, t)} className="text-zinc-400 hover:text-teal-600" aria-label={t.copy}><Copy className="h-3 w-3" /></button>
  </span>) : <span className="text-zinc-400">—</span>);

const FILTER_KEYS = ['started_from', 'started_to', 'feature', 'status', 'model', 'actor_user_id', 'warning_state', 'entry_point', 'mode', 'records', 'q', 'sort', 'skip'];
const isoLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const Sel = ({ k, label, value, onChange, options, render, allLabel }) => (
  <label className="flex flex-col gap-0.5 text-[11px] text-zinc-500">{label}
    <select data-testid={`aiops-filter-${k}`} value={value} onChange={(e) => onChange(k, e.target.value)} className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-[12px] text-zinc-800">
      <option value="">{allLabel}</option>{(options || []).map((o) => <option key={o.value ?? o} value={o.value ?? o}>{render ? render(o) : (o.label ?? o)}</option>)}
    </select></label>);

// Main list columns. 範囲 (mode/scope) and 実行ID／トレースID were removed from this
// table on purpose: both carried little information per row while claiming real width,
// which starved 警告・回復 until CJK badge text wrapped one character per line. Both
// values stay in the API payload, on the detail page (with copy controls) and in the
// audit CSV — this is a presentation change only. `colScope` / `colIds` remain in the
// i18n map for the CSV/detail vocabulary; do not re-add them as list columns.
const COLS = [
  ['colTime', 'whitespace-nowrap'],
  ['colFeature', ''],
  ['colType', ''],
  ['colStatus', ''],
  ['colActor', ''],
  ['colModel', ''],
  ['colDuration', 'whitespace-nowrap'],
  // The recovered width goes here. min-w keeps whole badges on one line at desktop
  // width; w-[26%] stops the auto table layout from collapsing this column again.
  ['colFlags', 'w-[26%] min-w-[220px]'],
];

export default function AiOperationLogs() {
  const [lang, toggleLang, t] = useLang();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const params = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, sp.get(k) || ''])), [sp]);
  const [opts, setOpts] = useState(null);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [state, setState] = useState('loading');
  const [exporting, setExporting] = useState(false);
  const limit = 25;

  const setParam = (k, v) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); if (k !== 'skip') n.delete('skip'); setSp(n, { replace: false }); };
  const query = useMemo(() => { const q = {}; FILTER_KEYS.forEach((k) => { if (params[k]) q[k] = params[k]; }); q.limit = limit; if (q.started_from) q.started_from = new Date(q.started_from).toISOString(); if (q.started_to) q.started_to = new Date(q.started_to).toISOString(); return q; }, [params]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const [l, s] = await Promise.all([axios.get(`${API}/page`, { params: query, ...authCfg() }), axios.get(`${API}/summary`, { params: { ...query, limit: undefined, skip: undefined, sort: undefined }, ...authCfg() })]);
      setData(l.data); setSummary(s.data); setState('ok');
    } catch (e) { setState(e?.response?.status === 422 ? `422:${e.response.data?.detail || ''}` : 'error'); }
  }, [query]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/filter-options`, { params: { started_from: query.started_from, started_to: query.started_to, records: params.records || undefined }, ...authCfg() }).then((r) => setOpts(r.data)).catch(() => setOpts({})); }, [query.started_from, query.started_to, params.records]);

  const flabels = opts?.feature_labels;

  // CSV export: sends exactly the filters/scope/sort the screen is showing. The server
  // re-verifies the capability, so this button is UX only, not access control.
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const { limit: _l, skip: _s, ...f } = query;
      const r = await axios.get(`${API}/export.csv`, { params: { ...f, lang }, responseType: 'blob', ...authCfg() });
      const rows = r.headers['x-export-rows'];
      const truncated = String(r.headers['x-export-truncated']) === 'true';
      const cd = r.headers['content-disposition'] || '';
      const name = (/filename="([^"]+)"/.exec(cd) || [])[1] || 'ai_execution_log.csv';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      if (truncated) toast.warning(`${t.exportTruncated} (${rows} / ${r.headers['x-export-limit']})`);
      else toast.success(`${t.exportDone}: ${rows ?? ''}`);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? t.exportForbidden : t.exportFailed);
    } finally { setExporting(false); }
  }, [query, lang, t]);


  const skip = Number(params.skip || 0);
  const total = data?.total || 0;
  const sel = (k, label, options, render) => <Sel k={k} label={label} value={params[k]} onChange={setParam} options={options} render={render} allLabel={t.all} />;

  return (
    <div className="space-y-4" data-testid="aiops-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/settings/ai-console" className="inline-flex items-center gap-1 text-[12px] text-teal-700 hover:underline" data-testid="aiops-back-console"><ArrowLeft className="h-3 w-3" />{t.backToConsole}</Link>
          <h1 className="mt-1 text-xl font-bold text-zinc-900">{t.title}</h1>
          <p className="mt-1 max-w-3xl text-[13px] text-zinc-600">{t.desc}</p>
          <p className="mt-2 inline-flex items-start gap-1.5 rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1.5 text-[12px] text-teal-900" data-testid="aiops-security-notice"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t.notice}</p>
        </div>
        <Button variant="outline" size="sm" onClick={toggleLang} data-testid="aiops-lang-toggle">{lang === 'en' ? '日本語' : 'English'}</Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-9" data-testid="aiops-counters">
          {[['total', summary.total], ['complete', summary.by_status?.complete || 0], ['complete_with_warnings', summary.by_status?.complete_with_warnings || 0],
            ['failed', summary.by_status?.failed || 0], ['running', summary.by_status?.running || 0], ['cancelled', summary.by_status?.cancelled || 0],
            ['rejected', summary.by_status?.rejected || 0],
            ['parser_issues', summary.parser_issues], ['recovery_used', summary.recovery_used]].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-zinc-200 bg-white px-3 py-2" data-testid={`aiops-counter-${k}`}>
              <div className="text-[11px] text-zinc-500">{t[k]}</div><div className="text-lg font-bold text-zinc-900">{v}</div>
            </div>))}
          {summary.partial && <div className="col-span-full text-[11px] text-amber-700">{t.partialCounts}（{summary.considered}）</div>}
        </div>)}

      <div className="rounded-lg border border-zinc-200 bg-white p-3" data-testid="aiops-filter-bar">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-6">
          <label className="flex flex-col gap-0.5 text-[11px] text-zinc-500">{t.from}<input data-testid="aiops-filter-from" type="datetime-local" value={params.started_from} onChange={(e) => setParam('started_from', e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-[12px]" /></label>
          <label className="flex flex-col gap-0.5 text-[11px] text-zinc-500">{t.to}<input data-testid="aiops-filter-to" type="datetime-local" value={params.started_to} onChange={(e) => setParam('started_to', e.target.value)} className="h-8 rounded-md border border-zinc-200 px-2 text-[12px]" /></label>
          {sel('feature', t.feature, opts?.feature_key, (o) => featureLabel(o, lang, flabels))}
          {sel('status', t.status, opts?.status, (o) => t[o] || o)}
          {sel('model', t.model, opts?.models)}
          {sel('actor_user_id', t.actor, (opts?.actors || []).map((a) => ({ value: a.user_id, label: a.display || a.user_id })))}
          {sel('warning_state', t.warningState, [{ value: 'warnings', label: t.warningsOnly }, { value: 'failures', label: t.failuresOnly }, { value: 'parser', label: t.parserOnly }, { value: 'none', label: t.noWarnings }])}
          {sel('records', t.records, [{ value: 'logical', label: t.recLogical }, { value: 'calls', label: t.recCalls }, { value: 'all', label: t.recAll }])}
          {sel('entry_point', t.entryPoint, opts?.entry_point)}
          {sel('mode', t.scope, opts?.mode)}
          {sel('sort', t.sort, ['newest', 'oldest', 'duration_desc', 'duration_asc'].map((s) => ({ value: s, label: t[s] })))}
          <label className="col-span-2 flex flex-col gap-0.5 text-[11px] text-zinc-500">{t.search}
            <input data-testid="aiops-filter-q" value={params.q} onChange={(e) => setParam('q', e.target.value)} placeholder="min 6 chars" className="h-8 rounded-md border border-zinc-200 px-2 font-mono text-[12px]" /></label>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" data-testid="aiops-reset" onClick={() => { const d = new Date(); setSp(new URLSearchParams({ started_from: isoLocal(new Date(d.getTime() - 30 * 86400000)) })); }}><RotateCcw className="mr-1 h-3 w-3" />{t.reset}</Button>
          <Button variant="outline" size="sm" data-testid="aiops-reload" onClick={load}><RefreshCw className="mr-1 h-3 w-3" />{t.retry}</Button>
          <Button variant="outline" size="sm" data-testid="aiops-export-csv" disabled={exporting} onClick={exportCsv}><Download className="mr-1 h-3 w-3" />{exporting ? t.exporting : t.exportCsv}</Button>
          <span className="text-[11px] text-zinc-500" data-testid="aiops-records-note">{t.recNote}</span>
        </div>
        <p className="mt-1 text-[11px] text-zinc-500" data-testid="aiops-export-note">{t.exportNote}</p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
        {state === 'loading' && <div className="p-6 text-[13px] text-zinc-500" data-testid="aiops-loading">…</div>}
        {state === 'error' && <div className="p-6 text-[13px] text-rose-700" data-testid="aiops-error">{t.loadError} <Button size="sm" variant="outline" onClick={load}>{t.retry}</Button></div>}
        {state.startsWith('422') && <div className="p-6 text-[13px] text-amber-800" data-testid="aiops-422">{state.slice(4)}</div>}
        {state === 'ok' && data.items.length === 0 && <div className="p-8 text-center text-[13px] text-zinc-500" data-testid="aiops-empty">{t.empty}</div>}
        {state === 'ok' && data.items.length > 0 && (
          // min-w keeps the auto table layout from crushing columns at narrow widths:
          // the wrapper's overflow-x-auto scrolls instead of breaking CJK text mid-word.
          <table className="w-full min-w-[900px] text-[12px]" data-testid="aiops-table">
            <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-wide text-zinc-500">
              <tr>{COLS.map(([k, cls]) => <th key={k} data-testid={`aiops-th-${k}`} className={`whitespace-nowrap px-3 py-2 font-semibold ${cls}`}>{t[k]}</th>)}</tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} data-testid="aiops-row" className="cursor-pointer border-t border-zinc-100 hover:bg-teal-50/40" onClick={() => navigate(`/settings/ai-operations/${r.id}?${sp.toString()}`)}>
                  <td className="whitespace-nowrap px-3 py-2 text-zinc-700">{fmtTime(r.started_at, lang)}</td>
                  <td className="px-3 py-2">{featureLabel(r.feature_key, lang, flabels)}{r.role === 'child_call' && <div className="text-[10px] text-violet-700" data-testid="aiops-role-child">{t.roleChild}</div>}</td>
                  <td className="px-3 py-2 text-zinc-600">{r.operation_type || '—'}<div className="text-[11px] text-zinc-400">{r.entry_point || ''}</div></td>
                  <td className="px-3 py-2"><StatusBadge status={r.status} t={t} />{r.terminal?.reason && <div className="text-[10px] text-zinc-500" data-testid="aiops-terminal-reason">{r.terminal.reason}</div>}</td>
                  <td className="px-3 py-2">{r.actor?.display || r.actor?.user_id || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{(r.models || []).join(', ') || r.terminal?.resolved_model || <span className="italic text-zinc-400">{t.notRecorded}</span>}<div className="text-[10px] text-zinc-400">{r.model_calls ? `${r.model_calls} calls` : ''}{r.terminal?.attempts ? ` · ${t.attemptsCount} ${r.terminal.attempts}` : ''}</div></td>
                  <td className="whitespace-nowrap px-3 py-2">{fmtDuration(r.duration_ms) || '—'}</td>
                  <td className="px-3 py-2" data-testid="aiops-flags-cell"><div className="flex flex-wrap items-center gap-1">{(r.badges || []).map((b) => <Badge key={b} code={b} lang={lang} />)}</div></td>
                </tr>))}
            </tbody>
          </table>)}
      </div>
      {state === 'ok' && (
        <div className="flex items-center justify-between text-[12px] text-zinc-600" data-testid="aiops-pagination">
          <span>{skip + 1}–{Math.min(skip + limit, total)} {t.of} {total}{data.total_capped ? '+' : ''} {t.rows}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={skip === 0} data-testid="aiops-prev" onClick={() => setParam('skip', String(Math.max(0, skip - limit)) || '')}>{t.prev}</Button>
            <Button size="sm" variant="outline" disabled={skip + limit >= total} data-testid="aiops-next" onClick={() => setParam('skip', String(skip + limit))}>{t.next}</Button>
          </div>
        </div>)}
    </div>
  );
}
